import { useEffect, useState } from "react";
import { Autocomplete, FormControl, InputLabel, MenuItem, Select, SelectChangeEvent, TextField } from "@mui/material";
import { faTrashCan } from "@fortawesome/free-solid-svg-icons";
import { useAppDispatch, useAppSelector } from "../../../store/hooks";
import { changeServiceData, deleteServiceData, selectActiveRobotVersion } from "../../../store/ses/sesRobotSlice";
import { clearModel, getInfoModel, selectFullModel } from "../../../store/modelSlice";
import useAccessRight from "../../../hooks/useAccessRight";
import useTranslate from "../../../hooks/useTranslate";
import { SEE, SES, SMC } from "../../../constants/accessRights";
import { ROBOT_SERVICE_TYPE_LIST } from "../../../constants/robotConfigLists";
import { colorPrimary, colorRed } from "../../../constants/colors";
import { ServiceTypeRobot } from "../../../types/cloudTypes";
import { RequestStatus } from "../../../types/statusTypes";
import ButtonIcon from "../../../ui/ButtonIcon/ButtonIcon";
import ProgressCircle from "../../MUI/ProgressCircle/ProgressCircle";
import { IServiceDataProps } from "./ServiceData.props";
import styles from "./ServiceData.module.scss";

const ServiceData = ({ data, serviceDataName, modelsList, changeFlgHandler }: IServiceDataProps): JSX.Element => {
	const [selectType, setSelectType] = useState<ServiceTypeRobot>(data.type); // тип
	const [inputModelName, setInputModelName] = useState<string>(""); // название модели
	const [inputClassname, setInputClassname] = useState<string>(""); // класс/сущность (param)
	const [classnamesList, setClassnamesList] = useState<string[]>([]); // список классов

	const dispatch = useAppDispatch();
	const fullModel = useAppSelector(selectFullModel); // store - модель
	const activeRobotVersion = useAppSelector(selectActiveRobotVersion); // store - версия активного робота

	const isAccess = useAccessRight(); // hook для проверки прав доступа
	const translate = useTranslate(); // hook для перевода текста

	// следим за названием и сервисными данными и вписываем значения в поля
	useEffect(() => {
		setSelectType(data.type);
		setInputModelName(data.model);
		setInputClassname(data.param);
	}, [serviceDataName, data]);

	// следим за полем названия модели
	useEffect(() => {
		// через пол-секунды бездействия после окончания ввода названия модели
		const handler = setTimeout(() => {
			// если в списке моделей есть имя модели и есть тип
			if (modelsList.includes(inputModelName) && ((selectType === "smc" && isAccess(SMC.INFO)) || (selectType === "see" && isAccess(SEE.INFO)))) {
				dispatch(clearModel()); // очищаем данные модели
				dispatch(getInfoModel({ modelName: inputModelName, serviceType: selectType, modelNameAsIs: true })); // получаем инфо о модели
			}
		}, 500);

		return () => {
			clearTimeout(handler); // сбрасываем timeout, если продолжается ввод названия модели
		};
	}, [inputModelName]);

	// следим за данными модели
	useEffect(() => {
		// если в списке моделей есть имя модели
		if (modelsList.includes(inputModelName)) {
			// пишем классы модели в зависимости от выбранного типа
			setClassnamesList(fullModel.fullModel[inputModelName.includes("-new") ? "future" : inputModelName.includes("-backup") ? "previous" : "current"]?.classes || []);
		}
	}, [fullModel.fullModel]);

	// обработчик изменения типа
	const changeTypeHandler = (e: SelectChangeEvent<ServiceTypeRobot>): void => {
		const value = e.target.value as ServiceTypeRobot;
		dispatch(changeServiceData({ serviceDataName, data: { field: "type", value } }));
		changeFlgHandler("servicedata"); // ставим флаг о несохраненных данных
	};

	// обработчик изменения модели
	const changeModelHandler = (): void => {
		if (data.model !== inputModelName) {
			dispatch(changeServiceData({ serviceDataName, data: { field: "model", value: inputModelName } }));
		}
	};

	// обработчик изменения класса/сущности
	const changeParamHandler = (): void => {
		if (data.param !== inputClassname) {
			dispatch(changeServiceData({ serviceDataName, data: { field: "param", value: inputClassname } }));
		}
	};

	// обработчик удаления сервисных данных
	const deleteServiceDataHandler = () => {
		dispatch(deleteServiceData({ serviceDataName }));
		changeFlgHandler("servicedata"); // ставим флаг о несохраненных данных
	};

	return (
		<div className={styles.container}>
			<div className={styles.block}>
				{/* тип */}
				<FormControl fullWidth sx={{ '.MuiInputLabel-root[data-shrink="false"]': { top: -8 }, ".MuiSelect-select": { paddingBlock: 0 } }} required>
					<InputLabel sx={{ fontSize: 13 }}>{translate("select_type")}</InputLabel>
					<Select
						label={translate("select_type")}
						value={selectType}
						onChange={changeTypeHandler}
						disabled={!isAccess(SES.ROBOT_EDIT) || activeRobotVersion !== "draft"}
						style={{ fontSize: 13, height: 33, color: colorPrimary, textAlign: "left" }}
					>
						{ROBOT_SERVICE_TYPE_LIST.map(({ type, translation }) => (
							<MenuItem key={type} value={type} sx={{ fontSize: 13, color: colorPrimary }}>
								{translate(translation)}
							</MenuItem>
						))}
					</Select>
				</FormControl>

				{/* удаление сервисных данных */}
				{isAccess(SES.ROBOT_EDIT) && activeRobotVersion === "draft" && (
					<ButtonIcon icon={faTrashCan} iconColor={colorRed} iconSize="1x" title={translate("button_delete")} onClick={deleteServiceDataHandler} />
				)}
			</div>

			{/* модель */}
			<FormControl fullWidth>
				<Autocomplete
					freeSolo
					options={modelsList}
					value={inputModelName}
					onChange={(_, value) => {
						setInputModelName(value ? value : "");
						changeFlgHandler("servicedata"); // ставим флаг о несохраненных данных
					}}
					onBlur={changeModelHandler}
					disabled={!isAccess(SES.ROBOT_EDIT) || activeRobotVersion !== "draft"}
					renderInput={(params) => (
						<TextField
							{...params}
							required
							label={translate("input_model")}
							onChange={(e) => {
								setInputModelName(e.target.value);
								changeFlgHandler("servicedata"); // ставим флаг о несохраненных данных
							}}
							error={!inputModelName}
							InputLabelProps={{
								style: {
									fontSize: 13,
								},
							}}
							// InputProps={{
							// 	...params.InputProps, // важно прокинуть параметры
							// 	endAdornment: (
							// 		<>
							// 			{clusterServer.status === RequestStatus.LOADING && <ProgressCircle isBtnDisabled /> }
							// 			{params.InputProps.endAdornment} {/* важно дописать параметры */}
							// 		</>
							// 	),
							// }}
							sx={{ '.MuiInputLabel-root[data-shrink="false"]': { top: -8 } }}
						/>
					)}
					sx={{
						".MuiInputBase-root": { height: 33, fontSize: 13, color: colorPrimary },
						".MuiInputBase-input": { marginTop: -1 },
					}}
					renderOption={(props, option) => {
						return (
							<span {...props} style={{ fontSize: 13, color: colorPrimary, textAlign: "left" }} key={option}>
								{option}
							</span>
						);
					}}
				/>
			</FormControl>

			{/* класс/сущность */}
			<FormControl fullWidth>
				<Autocomplete
					freeSolo
					options={classnamesList}
					value={inputClassname}
					onChange={(_, value) => {
						setInputClassname(value ? value : "");
						changeFlgHandler("servicedata"); // ставим флаг о несохраненных данных
					}}
					onBlur={changeParamHandler}
					disabled={!isAccess(SES.ROBOT_EDIT) || activeRobotVersion !== "draft"}
					renderInput={(params) => (
						<TextField
							{...params}
							required
							label={translate(selectType === "smc" ? "input_class" : "input_entity")}
							onChange={(e) => {
								setInputClassname(e.target.value);
								changeFlgHandler("servicedata"); // ставим флаг о несохраненных данных
							}}
							error={!inputClassname}
							InputLabelProps={{
								style: {
									fontSize: 13,
								},
							}}
							InputProps={{
								...params.InputProps, // важно прокинуть параметры
								endAdornment: (
									<>
										{fullModel.status === RequestStatus.LOADING && <ProgressCircle isBtnDisabled />}
										{params.InputProps.endAdornment} {/* важно дописать параметры */}
									</>
								),
							}}
							sx={{ '.MuiInputLabel-root[data-shrink="false"]': { top: -8 } }}
						/>
					)}
					sx={{
						".MuiInputBase-root": { height: 33, fontSize: 13, color: colorPrimary },
						".MuiInputBase-input": { marginTop: -1 },
					}}
					renderOption={(props, option) => {
						return (
							<span {...props} style={{ fontSize: 13, color: colorPrimary, textAlign: "left" }} key={option}>
								{option}
							</span>
						);
					}}
				/>
			</FormControl>
		</div>
	);
};

export default ServiceData;
